import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { TrendUp, TrendDown, Wallet, Receipt, Buildings } from '@phosphor-icons/react';
import { Amount } from '../contexts/AmountVisibilityContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (n) => '\u20B9' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 2 });
// Format function passed to <Amount> that keeps 2 decimals — matches P&L's existing format.
const fmt2 = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 2 });
const todayYM = () => new Date().toISOString().slice(0, 7);

export default function ProfitLossPage() {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [filterMode, setFilterMode] = useState('month');
  const [month, setMonth] = useState(todayYM());
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/companies`, { withCredentials: true }).then((r) => setCompanies(r.data));
  }, []);

  const run = async () => {
    setLoading(true);
    try {
      const params = {};
      if (companyId) params.company_id = companyId;
      if (filterMode === 'month') params.month = month;
      else if (filterMode === 'year') params.year = year;
      else {
        if (fromDate) params.from_date = fromDate;
        if (toDate) params.to_date = toDate;
      }
      const r = await axios.get(`${API_URL}/api/reports/profit-loss`, { params, withCredentials: true });
      setData(r.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { run(); /* eslint-disable-next-line */ }, [companyId, filterMode, month, year, fromDate, toDate]);

  const profit = data?.summary?.net_profit_invoiced || 0;
  const isProfit = profit >= 0;

  return (
    <div data-testid="pnl-page" className="space-y-6">
      <div>
        <h1 className="text-4xl font-bold tracking-tight mb-1" style={{ fontFamily: 'Chivo, sans-serif' }}>Profit & Loss Statement</h1>
        <p className="text-sm" style={{ color: 'var(--dx-text2)' }}>
          Compare invoiced income against expenses + salaries to see profitability
        </p>
      </div>

      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <Label className="text-xs">Company</Label>
            <Select value={companyId || '__all'} onValueChange={(v) => setCompanyId(v === '__all' ? '' : v)}>
              <SelectTrigger data-testid="pnl-company"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All Companies</SelectItem>
                {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Period</Label>
            <Select value={filterMode} onValueChange={setFilterMode}>
              <SelectTrigger data-testid="pnl-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="month">Month</SelectItem>
                <SelectItem value="year">Year</SelectItem>
                <SelectItem value="range">Date Range</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {filterMode === 'month' && (
            <div>
              <Label className="text-xs">Month</Label>
              <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="pnl-month" />
            </div>
          )}
          {filterMode === 'year' && (
            <div>
              <Label className="text-xs">Year</Label>
              <Input type="number" value={year} onChange={(e) => setYear(e.target.value)} data-testid="pnl-year" />
            </div>
          )}
          {filterMode === 'range' && (
            <>
              <div>
                <Label className="text-xs">From</Label>
                <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="pnl-from" />
              </div>
              <div>
                <Label className="text-xs">To</Label>
                <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="pnl-to" />
              </div>
            </>
          )}
        </div>
      </Card>

      {loading && <div className="text-sm text-gray-500">Loading…</div>}

      {data && (
        <>
          {/* Headline */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="p-4 border-l-4 border-l-emerald-500">
              <div className="text-xs uppercase tracking-wider text-gray-500 flex items-center gap-2"><TrendUp size={14} />Revenue (Invoiced)</div>
              <div className="text-2xl font-bold mt-2 text-emerald-700"><Amount value={data.income.total_invoiced} format={fmt2} /></div>
              <div className="text-[11px] text-gray-500 mt-1">{data.income.invoice_count} invoice(s)</div>
            </Card>
            <Card className="p-4 border-l-4 border-l-blue-500">
              <div className="text-xs uppercase tracking-wider text-gray-500 flex items-center gap-2"><Wallet size={14} />Collected</div>
              <div className="text-2xl font-bold mt-2 text-blue-700"><Amount value={data.income.total_paid} format={fmt2} /></div>
              <div className="text-[11px] text-gray-500 mt-1">Outstanding: <Amount value={data.income.outstanding} format={fmt2} /></div>
            </Card>
            <Card className="p-4 border-l-4 border-l-amber-500">
              <div className="text-xs uppercase tracking-wider text-gray-500 flex items-center gap-2"><Receipt size={14} />Total Costs</div>
              <div className="text-2xl font-bold mt-2 text-amber-700"><Amount value={data.summary.total_costs} format={fmt2} /></div>
              <div className="text-[11px] text-gray-500 mt-1">
                Expenses <Amount value={data.expenses.total} format={fmt2} /> · Salaries <Amount value={data.salaries.total} format={fmt2} />
              </div>
            </Card>
            <Card className={`p-4 border-l-4 ${isProfit ? 'border-l-emerald-600 bg-emerald-50/40' : 'border-l-rose-600 bg-rose-50/40'}`}>
              <div className="text-xs uppercase tracking-wider text-gray-500 flex items-center gap-2">
                {isProfit ? <TrendUp size={14} /> : <TrendDown size={14} />}
                Net {isProfit ? 'Profit' : 'Loss'}
              </div>
              <div className={`text-2xl font-bold mt-2 ${isProfit ? 'text-emerald-700' : 'text-rose-700'}`}><Amount value={profit} format={fmt2} /></div>
              <div className="text-[11px] text-gray-500 mt-1">
                Margin: {data.summary.profit_margin_pct}% · Realised: <Amount value={data.summary.net_profit_realized} format={fmt2} />
              </div>
            </Card>
          </div>

          {/* Detail */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="p-4">
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2"><Buildings size={14} />Income — By Company</h3>
              <Table>
                <TableHeader>
                  <TableRow style={{ background: 'var(--dx-bg)' }}>
                    <TableHead>#</TableHead><TableHead>Company</TableHead><TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.income.by_company.length === 0 ? (
                    <TableRow><TableCell colSpan={3} className="text-center py-6 text-gray-400">No invoices in this period.</TableCell></TableRow>
                  ) : data.income.by_company.map((c, i) => (
                    <TableRow key={c.company_id}>
                      <TableCell className="text-gray-400">{i + 1}</TableCell>
                      <TableCell>{c.company_name}</TableCell>
                      <TableCell className="text-right font-medium">{fmt(c.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>

            <Card className="p-4">
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2"><Receipt size={14} />Expenses — By Category</h3>
              <Table>
                <TableHeader>
                  <TableRow style={{ background: 'var(--dx-bg)' }}>
                    <TableHead>#</TableHead><TableHead>Category</TableHead><TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.expenses.by_category.length === 0 ? (
                    <TableRow><TableCell colSpan={3} className="text-center py-6 text-gray-400">No expenses logged.</TableCell></TableRow>
                  ) : data.expenses.by_category.map((c, i) => (
                    <TableRow key={c.category}>
                      <TableCell className="text-gray-400">{i + 1}</TableCell>
                      <TableCell>{c.category}</TableCell>
                      <TableCell className="text-right font-medium">{fmt(c.amount)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-gray-50">
                    <TableCell></TableCell>
                    <TableCell className="font-semibold">Salaries Paid</TableCell>
                    <TableCell className="text-right font-semibold">{fmt(data.salaries.total)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Card>
          </div>

          {/* Note */}
          <Card className="p-3 text-xs text-gray-500" style={{ background: '#FAFAF9' }}>
            <span className="font-semibold">Note:</span> Net Profit (Invoiced) considers all invoices issued in the period regardless of payment.
            "Realised" profit uses only the amount actually collected. Salaries pulled from finalised Payroll records.
          </Card>
        </>
      )}
    </div>
  );
}
